define('services/analyticsService',[
    'lib/lodash4',
    'lib/knockout',
    'moment',
    'services/apiCore',
    'services/authService',
    'services/loggingService',
    'models/AnalyticsData',
    'models/AnalyticsDataV5'
], function (
    _,
    ko,
    moment,
    apiCore,
    authService,
    log,
    AnalyticsData,
    AnalyticsDataV5
) {
    'use strict';

    // ===================================================================================
    // Private
    // ===================================================================================

    // TODO: remove after this gets to prod - for v5
    var QUERY_TYPE = {
        AGGREGATE: "aggregate",
        TIME_SERIES: "timeseries",
        METRIC_DISCOVERY: "metricDiscovery"
    };

    var URI_BASE = '/analytics/';

    // TODO: remove after this gets to prod - for v5
    var URI = '/analytics/query';


    /**
     * generates an array of analyticsData Objects from the results array
     * @param  {Array} resultsArray The array of result objects
     * @return {Array} analyticsDataArray
     */
    var generateAnalyticsDataArray = function (resultsArray, mediaType) {
        var analyticsDataArray = [];
        _.map(resultsArray, function (objectInArray) {
            if (!_.isUndefined(mediaType) && !_.isUndefined(objectInArray.group.mediaType) && objectInArray.group.mediaType !== mediaType) {
                return null;
            }
            analyticsDataArray.push(new AnalyticsData(objectInArray));
        });

        if(_.isEmpty(analyticsDataArray)){
            analyticsDataArray.push(new AnalyticsData());
        }

        return analyticsDataArray;
    };

    // TODO: remove after this gets to prod - for v5
    var generateAnalyticsDataArrayV5 = function (resultsArray) {
        var analyticsDataArray = [];
        _.map(resultsArray, function (objectInArray) {
            analyticsDataArray.push(new AnalyticsDataV5(objectInArray));
        });

        if(_.isEmpty(analyticsDataArray)){
            analyticsDataArray.push(new AnalyticsDataV5());
        }

        return analyticsDataArray;
    };

    /**
     * Do some quick sanity checks to make sure that an interval string
     * will be accepted by the analytics service.
     * @param  {String}  interval Interval string
     * @param  {Object}  options  optional argument
     * @return {Boolean}          return false if the simple tests do not pass
     */
    var isValidInterval = function(interval, options) {
        var intervals = interval.split("/");
        var start     = moment(intervals[0]);
        var end       = moment(intervals[1]);
        var granularity;
        if (options) {
            granularity = options.granularity;
        }

        /*
          Simple sanity check to alert user of analytics service that
          their request will fail if interval is not properly constructed.
         */
        if (granularity) {
            if (granularity === "P1D" || granularity === "P1W") {
                var startUtcHours = start.utc().hours();
                var endUtcHours = end.utc().hours();

                if (startUtcHours !== endUtcHours) {
                    return false;
                }
            }
        }

        /*
          Simple sanity check for current intervals. Every interval should pass this until granularity
          is capable of going lower than PT30M.
         */
        if (end.minutes() === 0 || end.minutes() === 30) {
            return true;
        } else {
            return false;
        }
    };

    // ===================================================================================
    // Service Definition
    // ===================================================================================

    var analyticsService = {
        /**
         * Builds the filter object and any other optional objects to be included in the payload query
         *
         * In depth documentation of the Analytics Service REST API can be found here:
         *
         * https://confluence.inin.com/display/Development/Analytics+API
         *
         * @param  {String} id             id of thing to query
         * @param  {String} endpoint       the endpoint after '/analytics/'
         * @param  {Array}  dimension_name the dimension name
         * @param  {String} interval       the interval to query
         * @param  {Object} options        the options object
         * @return {Object}                A Promise that returns a new AnalyticsData object created
         *                                   with the server response.
         */

        execAggregateQuery: function (id, endpoint, dimension, interval, options) {

            if (_.includes(endpoint, '.')) {
                // Use v5 function because we have a dimension, not an endpoint

                return this.execAggregateQueryV5(id, endpoint, dimension, interval, options);
            }

            var groupBy = ko.unwrap(dimension);

            var opts = _.assign({}, options, {
                // ensure that the options object has a filter attribute
                "filter": {
                    "type" : "and",
                    "clauses": [
                        {
                            "type": "or",
                            "predicates": [
                                {
                                    "dimension": groupBy[0],
                                    "value": id
                                }
                            ]
                        }
                    ]
                }
            });

            var mediaType;
            // Handle optional arguments
            // TODO: Will need to be extended when new optional arguments are added.
            if (options && options.mediaType) {
                mediaType = ko.unwrap(options.mediaType);
                if (!_.includes(endpoint, "observations")) {
                    opts.filter.predicates = [{
                        "dimension": "mediaType",
                        "value": mediaType
                    }];
                }
            }

            var analyticsDataResponse;
            return this.executeQuery(URI_BASE + endpoint, groupBy, interval, opts)
                .then(function (response) {
                    if (!_.isEmpty(response.results)) {
                        analyticsDataResponse = generateAnalyticsDataArray([_.head(response.results)]);
                        if (analyticsDataResponse && analyticsDataResponse.length > 0) {
                            return analyticsDataResponse[0];
                        }
                    }
                    
                    return new AnalyticsData();
                });
        },

        /**
         * Builds filter object from the list of parameters and then calls the executeQuery method.
         * It takes the response from the payload query and calls generateAnalyticsData with the results array.
         *
         * @param  {Array}  idArr - Array of Ids
         * @param  {String} endpoint - The analytics endpoint after '/analytics/' 
         * @param  {Object[]} dimensions - JSON array of the dimensions used to constrain
         *  the groupings, much like a SQL GROUPBY operation. Can be empty to group solely
         *  by time granularity.
         * @param  {String} interval - ISO8601 specified time interval, indicating the
         *  time range for the data requested
         *
         * @param  {Object} [options=undefined] - optional arguments
         * @param  {String} [options.granularity=undefined] - ISO8601 specified time
         *  period (eg. P1D is one day, PT1H is one hour, PT30M is thirty minutes).
         *  Omitting this property will have the analytics service return a default
         *  granularity of PT30M.
         * @param {String} [options.mediaType] - Setting this argument will determine the
         *  media type in which the returned metrics will represent (voice, web chat, etc.).
         *  Omitting this property will have the analytics service return metrics for the
         *  "voice" media type.
         *
         * @return {Promise} A Promise that returns an array of new AnalyticsData objects
         *  created from the server response.
         */
        execDisjunctionAggregateQuery: function (idArr, endpoint, dimension, interval, options) {

            if (_.includes(endpoint, '.')) {
                // Use v5 function because we have a dimension, not an endpoint

                return this.execDisjunctionAggregateQueryV5(idArr, endpoint, dimension, interval, options);
            }

            var groupBy = ko.unwrap(dimension);

            var opts = _.assign({}, options, {
                // ensure that the options object has a filter attribute
                "filter": {
                    "type" : "and",
                    "clauses": [
                        {
                            "type": "or",
                            "predicates": _.map(idArr, function (id) {
                                return {
                                    "dimension": groupBy[0],
                                    "value": id
                                };
                            })
                        }
                    ]
                }
            });

            var mediaType;
            // Handle optional arguments
            // TODO: Will need to be extended when new optional arguments are added.
            if (options && options.mediaType) {
                mediaType = ko.unwrap(options.mediaType);
                if (!_.includes(endpoint, "observations")) {
                    opts.filter.predicates = [{
                        "dimension": "mediaType",
                        "value": mediaType
                    }];
                }
            }

            var analyticsDataResponse;
            return this.executeQuery(URI_BASE + endpoint, groupBy, interval, opts)
                .then(function (response) {
                    analyticsDataResponse = generateAnalyticsDataArray(response.results, mediaType);
                    return analyticsDataResponse;

                });
        },

        /**
         * This method builds and sends the payload object and version
         * to the apiCore and on success return a response object.
         *
         * @param {Object} payload query object
         * @return {Object} the response from the server
         */
        executeQuery: function (uri, groupBy, interval, options) {
            var payload = _.assign({
                groupBy: groupBy,
                interval: interval
            }, options);

            if (!isValidInterval(interval, options)) {
                log.error("This interval you send to the analytics service is invalid", interval, options);
            }

            return apiCore.v2.post(uri, payload)
                .then(function (response) {
                    return response;
                });

        },

        // TODO: remove after this gets to prod - for v5
        execAggregateQueryV5: function (id, dataSourceName, dimensionName, interval, options) {

            var dimensions = ko.unwrap(dimensionName);
            var dataSource = ko.unwrap(dataSourceName);
            var opts = _.assign({}, options, {
                // ensure that the options object has a filter attribute
                filter: {
                    "type" : "dimensional",
                    "dimensionName"  : dimensions[0],
                    "dimensionValue" : id
                }
            });

            // Handle optional arguments
            if (options) {
                opts.granularity = ko.unwrap(options.granularity);
            }

            var analyticsDataResponse;
            return this.executeQueryV5(QUERY_TYPE.AGGREGATE, dataSource, dimensions, interval, opts)
                .then(function (response) {
                    analyticsDataResponse = new AnalyticsDataV5(_.head(response.results));
                    return analyticsDataResponse;

                });
        },

        // TODO: remove after this gets to prod - for v5eated from the server response.
        execDisjunctionAggregateQueryV5: function (idArr, dataSourceName, dimensionName, interval, options) {
            var dimensions = ko.unwrap(dimensionName);
            var dataSource = ko.unwrap(dataSourceName);

            var opts = _.assign({}, options, {
                // ensure that the options object has a filter attribute
                'filter': {
                    "type" : "or",
                    "filters": _.map(idArr, function (id) {
                        return {
                            "type" : "dimensional",
                            "dimensionName": dimensions[0],
                            "dimensionValue": id
                        };
                    })
                }
            });

            // Handle optional arguments
            // TODO: Will need to be extended when new optional arguments are added.
            // if (options) {
            //     opts.granularity = ko.unwrap(options.granularity);
            // }

            var analyticsDataResponse;
            return this.executeQuery(QUERY_TYPE.AGGREGATE, dataSource, dimensions, interval, opts)
                .then(function (response) {
                    analyticsDataResponse = generateAnalyticsDataArrayV5(response.results);
                    return analyticsDataResponse;

                });
        },

        // TODO: remove after this gets to prod - for v5
        executeQueryV5: function (type, dataSource, dimensions, interval, options) {
            var version = { version: "v5" };

            var payload = _.assign({
                qType: type,
                dataSource: dataSource,
                dimensions: dimensions,
                interval: interval
            }, options);

            if (!isValidInterval(interval, options)) {
                log.error("This interval you send to the analytics service is invalid", interval, options);
            }

            return apiCore.post(URI, payload, version)
                .then(function (response) {
                    return response;
                });

        },

        MEDIA_TYPES: {
            VOICE: "voice",
            CALLBACK: "callback",
            CHAT: "chat",
            EMAIL: "email",
            MESSAGE: "message"
        },

        /**
         * STAT_KEYS Provided by Analytics. See Call Flow & Metrics Page:
         *
         * https://confluence.inin.com/pages/viewpage.action?pageId=61452876
         *
         */
        STAT_KEYS: {
            ABANDONED: "tAbandon",
            ACW: "tAcw",
            ANSWERED: "tAnswered",
            CUSTOMERS_WAITING: "oWaiting",
            HANDLE: "tHandle",
            HELD: "tHeldComplete",
            INTERACTING: "oInteracting",
            IVR: "tIvr",
            OFFERED: "nOffered",
            OVERSLA: "nOverSla",
            SERVICE_LEVEL: "oServiceLevel",
            SERVICE_TARGET: "oServiceTarget",
            TALK: "tTalkComplete",
            TIME_IN_STATUS: "tIS", // tIS.<Status ID> ms  The time in status for the given status id. At status change time.
            TRANSFERRED: "nTransferred",
            WAIT_TIME: "tWait",
            ACTIVE_MEMBERS: "oActiveUsers",  // Number of active members
            MEMBERS: "oMemberUsers"         // Active members
        }
    };

    return analyticsService;

});

